import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Ruler, Phone, Shield, Heart, ChevronRight, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ProfileCompletionModal({ childName, missingFields, onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    maternal_height: '',
    paternal_height: '',
    emergency_contact: '',
    emergency_phone: '',
    medical_conditions: ''
  });

  const steps = [
    {
      id: 'parental_heights',
      title: "Parent / Guardian Heights",
      subtitle: "Used to predict your adult height and growth trajectory",
      icon: Ruler,
      show: missingFields.includes('maternal_height') || missingFields.includes('paternal_height'),
      fields: (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Mother's Height (cm)</label>
            <input
              type="number"
              step="0.1"
              value={formData.maternal_height}
              onChange={(e) => setFormData({ ...formData, maternal_height: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
              placeholder="e.g. 165"
              data-testid="maternal-height-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Enter in centimetres (e.g. 165 = 5ft 5in)</p>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Father's Height (cm)</label>
            <input
              type="number"
              step="0.1"
              value={formData.paternal_height}
              onChange={(e) => setFormData({ ...formData, paternal_height: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
              placeholder="e.g. 180"
              data-testid="paternal-height-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">Enter in centimetres (e.g. 180 = 5ft 11in)</p>
          </div>
        </div>
      )
    },
    {
      id: 'emergency',
      title: "Emergency Contact",
      subtitle: "In case we need to reach someone during training or events",
      icon: Phone,
      show: missingFields.includes('emergency_contact') || missingFields.includes('emergency_phone'),
      fields: (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Emergency Contact Name</label>
            <input
              type="text"
              value={formData.emergency_contact}
              onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
              placeholder="e.g. Sarah Smith (Mum)"
              data-testid="emergency-contact-input"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Emergency Contact Phone</label>
            <input
              type="tel"
              value={formData.emergency_phone}
              onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
              placeholder="e.g. 07700 900000"
              data-testid="emergency-phone-input"
            />
          </div>
        </div>
      )
    },
    {
      id: 'medical',
      title: "Medical Information",
      subtitle: "Any conditions, allergies or medications we should know about",
      icon: Heart,
      show: missingFields.includes('medical_conditions'),
      fields: (
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Medical Conditions / Allergies</label>
          <textarea
            value={formData.medical_conditions}
            onChange={(e) => setFormData({ ...formData, medical_conditions: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors min-h-[100px] resize-none"
            placeholder="Enter any medical conditions, allergies, or current medications. Type 'None' if not applicable."
            data-testid="medical-conditions-input"
          />
        </div>
      )
    }
  ];

  const activeSteps = steps.filter(s => s.show);
  const currentStep = activeSteps[step];
  const isLastStep = step === activeSteps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleSubmit();
    } else {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/my-profile/complete`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile completed! Your growth predictions will now be more accurate.');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/my-profile/dismiss-completion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
    onSkip();
  };

  if (!currentStep) return null;

  const StepIcon = currentStep.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" data-testid="profile-completion-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#02b5ff]/20 to-[#ff025f]/20 p-5 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#02b5ff]" />
              <span className="text-xs font-semibold text-[#02b5ff] uppercase tracking-wider">Complete Your Profile</span>
            </div>
            <button
              onClick={handleSkip}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="skip-profile-completion"
              title="Skip for now"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-zinc-400">
            Hi <span className="text-white font-medium">{childName}</span> — a few quick details to help us track your development accurately.
          </p>
        </div>

        {/* Step Progress */}
        {activeSteps.length > 1 && (
          <div className="px-5 pt-4 flex gap-1.5">
            {activeSteps.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-[#02b5ff]' : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
        )}

        {/* Step Content */}
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#02b5ff]/10 flex items-center justify-center">
              <StepIcon className="h-5 w-5 text-[#02b5ff]" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{currentStep.title}</h3>
              <p className="text-zinc-500 text-xs">{currentStep.subtitle}</p>
            </div>
          </div>

          {currentStep.fields}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="skip-for-now-btn"
          >
            Skip for now
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                type="button"
                onClick={() => setStep(step - 1)}
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 text-sm h-9 px-4"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={submitting}
              className="bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white text-sm h-9 px-5"
              data-testid="profile-next-btn"
            >
              {submitting ? 'Saving...' : isLastStep ? (
                <><CheckCircle className="h-4 w-4 mr-1.5" /> Save</>
              ) : (
                <>Continue <ChevronRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
